'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface FAQProps {
  className?: string;
}

const FAQ = ({ className = '' }: FAQProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: 'How quickly can you respond to my moving request?',
      answer: 'We offer 24/7 availability and can typically respond to inquiries within 30 minutes during business hours and within 2 hours for after-hours requests. For emergency moves, we can often accommodate same-day or next-day service.',
      category: 'Response Time'
    },
    {
      id: 2,
      question: 'What areas do you service in Kuwait?',
      answer: 'We primarily serve Farwaniya and surrounding districts, but we provide moving services throughout Kuwait. Our service area includes all major residential and commercial areas. Contact us to confirm coverage for your specific location.',
      category: 'Service Area'
    },
    {
      id: 3,
      question: 'Do you provide packing materials and services?',
      answer: 'Yes, we offer comprehensive packing services including high-quality packing materials, professional packing assistance, and specialized packing for fragile items. You can choose full-service packing or purchase materials for self-packing.',
      category: 'Services'
    },
    {
      id: 4,
      question: 'Are my belongings insured during the move?',
      answer: 'Absolutely. All moves are covered by our comprehensive insurance policy. We offer different coverage levels to suit your needs and the value of your belongings. Our team handles every item with professional care and attention.',
      category: 'Insurance'
    },
    {
      id: 5,
      question: 'How do I get an accurate moving quote?',
      answer: 'You can get an instant quote through our online calculator, call us at 6991 1181, or message us on WhatsApp. For the most accurate quote, we recommend a free in-home assessment where we evaluate your specific moving needs.',
      category: 'Pricing'
    },
    {
      id: 6,
      question: 'What payment methods do you accept?',
      answer: 'We accept cash, bank transfers, credit cards, and K-Net payments. Payment terms are flexible, with options for deposit and balance payment. We provide detailed invoices and receipts for all transactions.',
      category: 'Payment'
    },
    {
      id: 7,
      question: 'Can you handle special items like pianos or artwork?',
      answer: 'Yes, we specialize in moving delicate and valuable items including pianos, artwork, antiques, and electronics. Our team uses specialized equipment and techniques to ensure safe transport of all special items.',
      category: 'Special Items'
    },
    {
      id: 8,
      question: 'What if I need to reschedule my move?',
      answer: 'We understand that plans can change. You can reschedule your move by contacting us at least 48 hours in advance. We\'ll work with you to find a new date that fits your schedule without additional charges.',
      category: 'Scheduling'
    }
  ];

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-border rounded w-3/4 mx-auto"></div>
              <div className="h-4 bg-border rounded w-1/2 mx-auto"></div>
              <div className="space-y-3 mt-8">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-16 bg-card rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
            Frequently Asked <span className="text-secondary">Questions</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
            Find quick answers to common questions about our moving services and processes.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqItems.map((item) => (
            <div
              key={item.id}
              className="bg-card rounded-lg shadow-soft border border-border overflow-hidden transition-all duration-base hover:shadow-elevated"
            >
              <button
                onClick={() => toggleFAQ(item.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors duration-base"
                aria-expanded={openId === item.id}
              >
                <div className="flex-1 pr-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-body font-semibold rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-headline font-bold text-primary">
                    {item.question}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  <Icon
                    name={openId === item.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={24}
                    variant="solid"
                    className="text-secondary transition-transform duration-base"
                  />
                </div>
              </button>

              {openId === item.id && (
                <div className="px-6 pb-6 animate-fadeIn">
                  <p className="text-text-secondary font-body leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-text-secondary font-body mb-4">
            Still have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:69911181"
              className="flex items-center space-x-2 bg-secondary text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
            >
              <Icon name="PhoneIcon" size={20} variant="solid" />
              <span className="font-accent">6991 1181</span>
            </a>
            <a
              href="https://wa.me/96569911181"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-success text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
              <span>Chat on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;